<template>
  <button
    ref="button"
    :disabled="disabled"
    :title="showTooltip ? null : title"
    :aria-label="ariaLabel"
    class="square-icon-button--tooltip"
  >
    <slot name="icon"></slot>
    <div
      v-if="showTooltip"
      role="tooltip"
      class="accessible-tooltip"
      aria-labelledby="accessibleTooltipTitle"
    >
      <span id="accessibleTooltipTitle"> {{ title }} </span>
    </div>
  </button>
</template>

<script setup lang="ts">
import { computed, useTemplateRef, watchEffect } from "vue";
import { adjustTooltipPosition } from "@/helpers/helperFunctions";
import { useMediaQuery } from "@/helpers/useMediaQuery";
import { useElementHover, useFocus } from "@vueuse/core";

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    title?: string | null;
    ariaLabel?: string | null;
    showOnHover?: boolean | null;
  }>(),
  {
    disabled: false,
    title: null,
    ariaLabel: null,
    showOnHover: null,
  }
);

// some devices like chromeOS would show the tooltip on click
// those feature checks ensure uniform behavior across devices
const hasHoverEvent = useMediaQuery("(any-hover: hover)", true);
const hasFocusEvent = useMediaQuery("(any-pointer: fine)", true);

const showOnHover = computed<boolean>(
  () =>
    props.showOnHover !== false && // allow to fully disable the hover event
    hasHoverEvent.value &&
    (props.showOnHover || !props.disabled) // allows to show the tooltip on hover even if the button is disabled
);

const showOnFocus = computed<boolean>(
  // the default browser behavior would be to not allows focus on disabled buttons
  // but it doesn't cost much to enforce uniformity here
  () => hasFocusEvent.value && !props.disabled
);

const button = useTemplateRef<HTMLButtonElement>("button");
const isHovered = useElementHover(button);
const { focused: isFocused } = useFocus(button);

const showTooltip = computed(() => {
  if (showOnHover.value && isHovered.value) return true;
  if (showOnFocus.value && isFocused.value) return true;

  return false;
});

watchEffect(() => {
  if (showTooltip.value && button.value) {
    const element = button.value;
    if (!props.title) return; // check any title update to reposition the tooltip when the same button has dual state (e.g. play/pause or select/hand)
    setTimeout(() => adjustTooltipPosition(element), 0);
  }
});
</script>

<style lang="scss" scoped>
@use "@/assets/styles/animation.scss" as *;

.accessible-tooltip {
  opacity: 0;
  animation: fade-in 0.2s forwards;
}
</style>
